<template>
  <BaseModal 
    :show="isOpen" 
    :title="group ? 'Editar grupo' : 'Nuevo grupo'"
    :show-actions="true"
    :is-editing="!!group"
    :loading="saving"
    @close="$emit('close')"
    @save="save"
  >
    <!-- Content -->
    <div class="flex-1 overflow-y-auto space-y-3 p-3 sm:space-y-4 sm:p-4">
      <ModifierGroupBasicsSection v-model="form" />

      <ModifierGroupOptionsSection v-model:options="form.options" />
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { ModifierGroup, ModifierOption } from '@/types/menu';
import BaseModal from '@/components/common/BaseModal.vue';
import ModifierGroupBasicsSection from './ModifierGroupBasicsSection.vue';
import ModifierGroupOptionsSection from './ModifierGroupOptionsSection.vue';

const props = defineProps<{
  isOpen: boolean;
  group?: ModifierGroup | null;
  saving?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  save: [data: Omit<ModifierGroup, 'id'>];
}>();

const createDefaultOption = (): ModifierOption => ({
  name: '',
  price_delta: 0,
  is_active: true,
  sort_order: 0,
});

interface ModifierGroupFormState {
  name: string;
  description: string;
  is_active: boolean;
  required_default: boolean;
  min_select_default: number;
  max_select_default: number;
  sort_order: number;
  options: ModifierOption[];
}

const createInitialForm = (): ModifierGroupFormState => ({
  name: '',
  description: '',
  is_active: true,
  required_default: false,
  min_select_default: 0,
  max_select_default: 1,
  sort_order: 0,
  options: [createDefaultOption()]
});

const form = ref<ModifierGroupFormState>(createInitialForm());

const mapGroupToForm = (group: ModifierGroup): ModifierGroupFormState => ({
  name: group.name,
  description: group.description || '',
  is_active: group.is_active ?? true,
  required_default: group.required_default ?? false,
  min_select_default: group.min_select_default ?? 0,
  max_select_default: group.max_select_default ?? 1,
  sort_order: group.sort_order ?? 0,
  options: (group.options || []).map((option, index) => ({
    id: option.id,
    name: option.name,
    price_delta: Number(option.price_delta || 0),
    is_active: option.is_active ?? true,
    sort_order: option.sort_order ?? index
  }))
});

const resetForm = () => {
  form.value = createInitialForm();
};

const buildPayload = (): Omit<ModifierGroup, 'id'> => {
  const cleanedOptions = form.value.options
    .map((option, index) => ({
      name: option.name.trim(),
      price_delta: Number(option.price_delta || 0),
      is_active: option.is_active ?? true,
      sort_order: option.sort_order ?? index,
    }))
    .filter((option) => option.name.length > 0);

  if (!form.value.name.trim()) {
    throw new Error('El nombre del grupo es obligatorio');
  }

  if (form.value.max_select_default < form.value.min_select_default) {
    throw new Error('Max selección no puede ser menor que Min selección');
  }

  if (cleanedOptions.length === 0) {
    throw new Error('Agrega al menos una opción válida');
  }

  return {
    name: form.value.name.trim(),
    description: form.value.description?.trim() || undefined,
    is_active: form.value.is_active,
    required_default: form.value.required_default,
    min_select_default: Number(form.value.min_select_default || 0),
    max_select_default: Number(form.value.max_select_default || 1),
    sort_order: Number(form.value.sort_order || 0),
    options: cleanedOptions,
  };
};

const save = () => {
  try {
    const payload = buildPayload();
    emit('save', payload);
  } catch (error: any) {
    alert(error.message);
  }
};

watch(() => props.isOpen, (open) => {
  if (open) {
    if (props.group) {
      form.value = mapGroupToForm(props.group);
      if (form.value.options.length === 0) {
        form.value.options = [createDefaultOption()];
      }
    } else {
      resetForm();
    }
  }
});

// Expose form data for parent validation
defineExpose({
  buildPayload,
  resetForm,
});
</script>
